import * as moment from 'moment'
import { Injectable } from '@angular/core'
import { Params } from '@angular/router'

import { MembershipDefinition, Tag, Persona } from 'app/models'
import { Store } from './store'

@Injectable()
export class FilterService extends Store<MembershipDefinition[]> {

  private readonly role: string = 'role'
  private readonly seasonality: string = 'seasonality'

  private filteredMembershipDefintions: MembershipDefinition[]
  private membershipDefinitions: MembershipDefinition[]
  private persona: Persona

  public constructor() {
    super([])
  }

  public init(persona: Persona, membershipDefinitions: MembershipDefinition[]) {
    this.persona = persona
    this.membershipDefinitions = this.ageAndGenderFilter(membershipDefinitions)
    this.filteredMembershipDefintions = this.membershipDefinitions
    this.setCurrent(this.membershipDefinitions)
  }

  public filter(params: Params): void {
    const { role, seasonality } = params
    this.filteredMembershipDefintions = this.membershipDefinitions
    if (role) this.roleFilter(role)
    if (seasonality) this.seasonalityFilter(seasonality)
    this.setCurrent(this.filteredMembershipDefintions)
  }

  public get hasRoleTagVariations(): boolean {
    return this.hasTagOptionTypeVariations(this.role)
  }

  public get hasSeasonalityTagVariations(): boolean {
    return this.hasTagOptionTypeVariations(this.seasonality)
  }

  private ageAndGenderFilter(membershipDefinitions: MembershipDefinition[]): MembershipDefinition[] {
    return membershipDefinitions.filter((memdef) => {
      const ageTags = memdef.tags.filter((tag: Tag) => tag.tag_option_type.toLowerCase() === 'age')
      const genderTags: Tag[] = memdef.tags.filter((tag: Tag) => tag.tag_option_type.toLowerCase() === 'gender')
      return this.ageFilter(ageTags) && this.genderFilter(genderTags)
    })
  }

  private ageFilter(ageTags: Tag[]): boolean {
    return !ageTags.length || !!ageTags.find((ageTag) => {
      return (ageTag.tag_value_type === 'Tags::TagDateRangeValue' ? this.dateRangeFilter(ageTag) : this.numberRangeFilter(ageTag))
    })
  }

  private dateRangeFilter(tag: Tag): boolean {
    let { start_date, end_date } = tag.tag_value
    const birthdate = moment(this.persona.date_of_birth)
    if (start_date) start_date = moment.utc(start_date).format('YYYY-MM-DD')
    if (end_date) end_date = moment.utc(end_date).format('YYYY-MM-DD')

    if (start_date && end_date) return birthdate.isBetween(start_date, end_date, null, '[]')
    if (start_date) return birthdate.isSameOrAfter(start_date)
    if (end_date) return birthdate.isSameOrBefore(end_date)
  }

  private filterOnTagOptionType(tagOptionType: string, value: string): void {
    this.filteredMembershipDefintions = this.filteredMembershipDefintions.filter((memdef) => {
      const tagValues = memdef.tags
        .filter((t: Tag) => t.tag_option_type.toLowerCase() === tagOptionType.toLowerCase())
        .map((t: Tag) => t.value.toLowerCase())
      return !tagValues.length || tagValues.includes(value.toLowerCase())
    })
  }

  private genderFilter(tags: Tag[]): boolean {
    const { gender } = this.persona
    return !tags.length || !!tags.find((tag) => tag.tag_value.value.toLowerCase() === gender?.toLowerCase())
  }

  private hasTagOptionTypeVariations(tagOptionType: string): boolean {
    const tagValues = this.filteredMembershipDefintions.map((memdef) =>
      memdef.tags
        .filter((tag: Tag) => tag.tag_option_type.toLowerCase() === tagOptionType.toLowerCase())
        .map((tag: Tag) => tag.value.toLowerCase())
    )
    return this.compare(tagValues)
  }

  private compare(values: any[][]): boolean {
    if (values.length < 2) return false

    const first = values.pop()
    if (!first.length) this.compare(values)

    return !!values.find((value) => {
      if (!value.length) return false
      if (first.length !== value.length) return true
      return first.find((v) => !value.includes(v))
    })
  }

  private numberRangeFilter(tag: Tag): boolean {
    const { min, max } = tag.tag_value
    const age = moment().diff(this.persona.date_of_birth, 'years')
    if (min && max) return age >= min && age <= max
    if (min) return age >= min
    if (max) return age <= max
  }

  private roleFilter(role: string): void {
    this.filterOnTagOptionType(this.role, role)
  }

  private seasonalityFilter(seasonality: string): void {
    this.filterOnTagOptionType(this.seasonality, seasonality)
  }

}
